// Shared dispensing-cart business rules between the two screens that build a
// pharmacy cart against a structured prescription: the visit-pipeline
// PharmacyStage.tsx (one visit's own prescription, seeded automatically) and
// the standalone DispensingTab.tsx (today's queue of active prescriptions,
// picked one at a time). See the UI modernization assessment's 7th finding
// and the handoff's own §9 note on why this extraction exists.
//
// Deliberately NOT extracted here: `stockFor`/`findMatchingPrescriptionItem`
// (the two screens' batch-matching strategies) and the substitution picker.
// Those are genuinely different behaviours, not incidental duplication —
// PharmacyStage's stockFor only ever resolves a batch whose name matches the
// prescribed drug (see that file's own header comment), so it can never
// produce a genuine drug-identity substitution the way DispensingTab's
// SubstitutePicker explicitly supports. Forcing those into one function
// would either remove a real capability from DispensingTab or silently add
// one to PharmacyStage that its own UI has no picker for. Everything below
// is instead the subset of rules that are byte-for-byte identical business
// logic in both files: cart totals, the Schedule H1/X prescriber gate, GST-
// option resolution/reconciliation, and the two backend-enforced "resolve
// before checkout" gates (quantity overage, off-prescription line) that
// exist purely because of what routes/billing.js's POST /pharmacy/create
// hard-blocks — not a per-screen judgment call either file gets to make
// differently.
//
// Every function here takes the MINIMAL shape a cart line needs
// (DispensingCartLine) rather than either screen's own full CartItem type,
// so each screen's extra fields (medicine_id, substitution_reason, the
// batch/expiry display fields, ...) stay exactly where they are.

export interface DispensingCartLine {
  inventory_id: number;
  qty: number;
  mrp: number;
  gst: number;
  schedule: string;
  prescription_item_id?: number;
  quantity_override_reason?: string;
  off_prescription_reason?: string;
}

export interface CartTotals {
  subtotal: number;
  gstAmt: number;
  total: number;
}

export function cartTotals(items: DispensingCartLine[], gstEnabled: boolean): CartTotals {
  const subtotal = items.reduce((s, i) => s + i.qty * i.mrp, 0);
  const gstAmt = gstEnabled ? items.reduce((s, i) => s + (i.qty * i.mrp * i.gst) / 100, 0) : 0;
  return { subtotal, gstAmt, total: subtotal + gstAmt };
}

/** Schedule H1/X items require a prescriber name/reg. no. for the statutory register. */
export function needsPrescriberFor(items: Pick<DispensingCartLine, 'schedule'>[]): boolean {
  return items.some((i) => ['H1', 'X'].includes(i.schedule));
}

/**
 * The medicine's own stored GST rate is only a valid starting value when
 * it's actually one of the admin's configured percentage options —
 * otherwise a <select> built from those options would silently default to
 * its first entry while the line quietly kept the real, unlisted rate, so
 * the dropdown and the computed total would disagree. Both screens' own
 * cart-line builders (DispensingTab's addItem, PharmacyStage's lineFor)
 * call this rather than duplicating the fallback.
 */
export function resolveGstPercent(medGstPercent: number, gstPercentageOptions: number[]): number {
  return !gstPercentageOptions.length || gstPercentageOptions.includes(medGstPercent)
    ? medGstPercent
    : gstPercentageOptions[0] ?? 0;
}

/**
 * Re-pins every cart line's gst to a still-valid configured option whenever
 * the admin's percentage list changes — an item added under a percentage the
 * admin has since removed would otherwise keep billing at a rate the
 * dropdown no longer offers. Returns the SAME array reference when nothing
 * needed to move, so a caller's setItems(prev => reconcileGstOptions(prev, ...))
 * doesn't cause a pointless re-render every time the effect re-runs.
 */
export function reconcileGstOptions<T extends DispensingCartLine>(items: T[], gstPercentageOptions: number[]): T[] {
  const options = gstPercentageOptions.map(String);
  if (!options.length) return items;
  let changed = false;
  const next = items.map((it) => {
    if (options.includes(String(it.gst))) return it;
    changed = true;
    return { ...it, gst: parseFloat(options[0] as string) };
  });
  return changed ? next : items;
}

/**
 * Cart lines linked to a prescribed item whose quantity now exceeds what
 * remains on that line. routes/billing.js's POST /pharmacy/create
 * hard-blocks these unless quantity_override_reason is set (see
 * migration_dispensing_quantity_override.sql) — null `remaining` (a PRN/SOS
 * item with no derivable total) is never flagged, matching that same guard.
 */
export function unresolvedQuantityOverages<T extends DispensingCartLine>(
  items: T[],
  remainingByPrescriptionItemId: Map<number, number | null>
): T[] {
  return items.filter((i) => {
    if (i.prescription_item_id == null || i.quantity_override_reason) return false;
    const remaining = remainingByPrescriptionItemId.get(i.prescription_item_id);
    return remaining != null && i.qty > remaining;
  });
}

/**
 * Cart lines with no prescription_item_id at all — a medicine added outside
 * the doctor's prescribed list, not a substitution of a prescribed one.
 * routes/billing.js's POST /pharmacy/create hard-blocks these unless
 * off_prescription_reason is set (see
 * migration_dispensing_off_prescription_reason.sql).
 */
export function unresolvedOffPrescription<T extends DispensingCartLine>(items: T[]): T[] {
  return items.filter((i) => i.prescription_item_id == null && !i.off_prescription_reason);
}

/**
 * True when the cart has an off-prescription line (see above) WHILE the
 * prescription still has an unfulfilled line that isn't in the cart at all —
 * the shape a pharmacist gets into by picking the substitute drug from the
 * free "Add medicine" list instead of clicking that unfulfilled line's own
 * 🔀 Substitute button. Both screens' name-matching (findMatchingPrescriptionItem
 * / PharmacyStage's own stockFor) already tries to auto-link a free-add pick
 * back to a prescribed line by drug name, so this only fires when that match
 * genuinely failed (e.g. "Paracetamol" doesn't resemble "Metformin") — the
 * off-prescription line then never counts against what the doctor
 * prescribed and isn't recorded as a substitution at all (no
 * pharmacy_substitution_reviews row — see substitutionReview.js), silently
 * leaving that prescribed line "still owed" forever from the patient's
 * perspective. Advisory only, never blocks: off-prescription is a genuine,
 * legitimate, tracked path in its own right (an add-on the doctor verbally
 * approved) — this just nudges toward the better-tracked alternative when
 * it looks like that's what was actually intended.
 */
export function offPrescriptionMayBeMissedSubstitution<T extends DispensingCartLine>(
  items: T[],
  prescriptionItems: Array<{ id: number; remaining_quantity: number | null }>
): boolean {
  if (!items.some((i) => i.prescription_item_id == null)) return false;
  const cartedIds = new Set(items.map((i) => i.prescription_item_id).filter((id): id is number => id != null));
  return prescriptionItems.some((it) => !cartedIds.has(it.id) && it.remaining_quantity != null && it.remaining_quantity > 0);
}

/**
 * Applies one shared free-text reason to every cart line named in
 * `targetInventoryIds` — the "Apply" action on both screens' quantity-
 * overage and off-prescription banners. `field` picks which reason column;
 * both banners use the same one-shared-field-for-the-whole-cart pattern
 * rather than one input per line.
 */
export function applyReasonToLines<T extends DispensingCartLine>(
  items: T[],
  targetInventoryIds: Set<number>,
  field: 'quantity_override_reason' | 'off_prescription_reason',
  reason: string
): T[] {
  return items.map((it) => (targetInventoryIds.has(it.inventory_id) ? { ...it, [field]: reason } : it));
}
